import React, {useEffect, useMemo} from 'react'
import {Button, Icon} from '@therms/atalaya'
import {ReportsList} from '@src/modules/Locations/components/ViewLocation/components/Reports/ReportsList'
import {
  RPCDocs,
  RPCs,
  useLocationContext,
  UserAnPCheck,
  useRPC,
  useToggle,
  useUrlState,
  useUrlStateDebounced,
} from '@therms/react-modules'
import {NewReportModal} from '@src/modules/Reports/shared/NewReport'
import {BulkDownloadReportsContextMenu} from '@src/modules/Reports/shared/components/BulkDownloadReportsContextMenu'
import {useSetState, useUpdateEffect} from 'ahooks'
import {ReportsSearchFilters} from '@src/modules/Reports/router/screens/Index/components/ReportsSearchFilters'
import {IoMdAdd} from 'react-icons/io'

interface ReportsIndexProps {
  locationId: string
}

function ReportsIndex({locationId}: ReportsIndexProps) {
  const {location} = useLocationContext(true)
  const [showNewReportModal, toggleNewReportModal] = useToggle(false)
  const [searchTextValue, searchText, setSearchText] = useUrlStateDebounced(
    'locreportsrch',
    '',
  )

  const [state, setState] = useSetState<{
    selectedActivityTypeIds?: string[] | undefined
    selectedDateTimeRange?: {
      start?: string | undefined
      end?: string | undefined
    }
    selectedReportTypeIds?: string[] | undefined
    selectedUserIds?: string[] | undefined
    selectedEventRefIds?: string[] | undefined
    selectedLocationRefIds?: string[] | undefined
    selectedRecordRefIds?: string[] | undefined
    selectedUserRefIds?: string[] | undefined
  }>({
    selectedActivityTypeIds: undefined,
    selectedDateTimeRange: {end: undefined, start: undefined},
    selectedEventRefIds: undefined,
    selectedLocationRefIds: undefined,
    selectedRecordRefIds: undefined,
    selectedReportTypeIds: undefined,
    selectedUserIds: undefined,
    selectedUserRefIds: undefined,
  })

  const [urlState, setUrlState] = useUrlState('locreportfilters', state)

  useEffect(() => {
    if (urlState) {
      setState(urlState)
    }
  }, [])

  useUpdateEffect(() => {
    setUrlState(state)
  }, [state])

  const {data: activityTags} = useRPC<
    RPCDocs.Tags.GetActivityTypeTagsList.v1.Args,
    RPCDocs.Tags.GetActivityTypeTagsList.v1.Data
  >(
    RPCs.Tags.GetActivityTypeTagsList.v1,
    {
      idFilter: state.selectedActivityTypeIds,
    },
    {fire: !!state.selectedActivityTypeIds?.length},
  )

  const tagTitles = useMemo(() => {
    if (!state.selectedActivityTypeIds?.length) return undefined

    return activityTags?.tags?.map((tag) => tag.title) || []
  }, [activityTags, state.selectedActivityTypeIds?.length])

  const filters: Record<string, any> = {
    activityTypeTagsFilter: tagTitles?.length ? tagTitles : undefined,
    authorUserIdsFilter: urlState.selectedUserIds?.length
      ? urlState.selectedUserIds
      : undefined,
    fromDate: urlState.selectedDateTimeRange?.start || undefined,
    refs: undefined,
    reportTypeIdsFilter: urlState.selectedReportTypeIds?.length
      ? urlState.selectedReportTypeIds
      : undefined,
    sort: ['date', 'desc'],
    state: undefined,
    toDate: urlState.selectedDateTimeRange?.end || undefined,
  }

  useMemo(() => {
    if (
      urlState.selectedEventRefIds?.length ||
      urlState.selectedRecordRefIds?.length ||
      urlState.selectedUserRefIds?.length
    ) {
      filters.refs = []
    }

    if (urlState.selectedEventRefIds?.length) {
      filters.refs = [...filters.refs, ...urlState.selectedEventRefIds]
    }
    if (urlState.selectedRecordRefIds?.length) {
      filters.refs = [...filters.refs, ...urlState.selectedRecordRefIds]
    }
    if (urlState.selectedUserRefIds?.length) {
      filters.refs = [...filters.refs, ...urlState.selectedUserRefIds]
    }
  }, [filters])

  const bulkDownloadArgs = useMemo(
    () => ({
      refs: [locationId],
      searchText,
      state: undefined,
    }),
    [locationId],
  )

  return (
    <div className="pr-xs relative">
      <div className="gap-x-xs flex flex-wrap-reverse justify-between">
        <div className="grow">
          <ReportsSearchFilters
            filtersState={urlState}
            setFiltersState={setState}
            searchText={searchTextValue}
            setSearchText={setSearchText}
            showLocationFilter={false}
          />
        </div>

        <div className="gap-x-xxs ml-auto flex min-w-max shrink items-start">
          <UserAnPCheck
            check={(user) => user.anp.role.ops.access.canDownloadAndExportData}
          >
            <BulkDownloadReportsContextMenu args={bulkDownloadArgs} />
          </UserAnPCheck>

          {location?.active && (
            <Button onClick={toggleNewReportModal} variant="main">
              <Icon Icon={IoMdAdd} size="sm" /> <span>New Report</span>
            </Button>
          )}
        </div>
      </div>

      <div className="mt-sm">
        <ReportsList
          filters={filters}
          locationId={locationId}
          searchText={searchText}
        />
      </div>

      {showNewReportModal && (
        <NewReportModal closeHandler={toggleNewReportModal} />
      )}
    </div>
  )
}

export {ReportsIndex}
